/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import HeaderInfo from "../components/common/HeaderInfo";
import DataTable from "../components/common/DataTable";
import toast from "react-hot-toast";
import {
  Edit,
  LocationIcon,
  AssignedVP,
  AssignedVP2,
  Incharge,
} from "../components/icons";
import { ArrowLeftRight, Eye } from "lucide-react";
import CampusDetailsModal from "../components/modals/CampusDetailsModal";
import CampusModal from "../components/modals/CampusModal";
import AssignCampusRoleModal from "../components/modals/AssignCampusRoleModal";
import DeleteModal from "../components/modals/DeleteModal";
import {
  getApiErrorMessage,
  isSessionExpiredApiError,
} from "../utils/authSession";
import {
  useAssignCampusVPMutation,
  useAssignStoreInchargeMutation,
  useUpdateCampusVPMutation,
  useUpdateStoreInchargeMutation,
  useCreateCampusMutation,
  useGetCampusesQuery,
  useSwapCampusRolesMutation,
  useUpdateCampusMutation,
} from "../store/api";

export default function Campuses() {
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedCampus, setSelectedCampus] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignRoleType, setAssignRoleType] = useState("vp");
  const [assignCampus, setAssignCampus] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsId, setDetailsId] = useState(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading } = useGetCampusesQuery({
    page,
    search: debouncedSearch,
  });

  const [createCampus, { isLoading: isCreating }] = useCreateCampusMutation();
  const [updateCampus, { isLoading: isUpdating }] = useUpdateCampusMutation();
  const [assignCampusVP, { isLoading: isAssigningVP }] =
    useAssignCampusVPMutation();
  const [assignStoreIncharge, { isLoading: isAssigningIncharge }] =
    useAssignStoreInchargeMutation();
  const [updateCampusVP, { isLoading: isUpdatingVP }] =
    useUpdateCampusVPMutation();
  const [updateStoreIncharge, { isLoading: isUpdatingIncharge }] =
    useUpdateStoreInchargeMutation();
  const [swapCampusRoles, { isLoading: isSwappingRoles }] =
    useSwapCampusRolesMutation();

  const isSubmitting = isCreating || isUpdating;
  const campuses = data?.data?.campuses || [];
  const filteredCampuses = campuses;
  const totalPages = data?.data?.totalPages || Math.ceil((data?.data?.nbHits || 0) / 10) || 1;

  function handleNext() {
    if (page < totalPages) setPage((p) => p + 1);
  }

  function handlePrev() {
    if (page > 1) setPage((p) => p - 1);
  }

  function handleOpenEdit(row) {
    setSelectedCampus(row);
    setModalMode("edit");
    setModalOpen(true);
  }

  function handleCloseModal() {
    setModalOpen(false);
    setSelectedCampus(null);
    setModalMode("add");
  }

  function handleOpenAdd() {
    setSelectedCampus(null);
    setModalMode("add");
    setModalOpen(true);
  }

  function handleOpenDelete(row) {
    setDeleteRow(row);
    setDeleteOpen(true);
  }

  function handleCloseDelete() {
    setDeleteOpen(false);
    setDeleteRow(null);
  }

  function handleOpenAssign(row, roleType) {
    setAssignCampus({ id: row.id, name: row.name });
    setAssignRoleType(roleType);
    setAssignModalOpen(true);
  }

  function handleOpenDetails(row) {
    setDetailsId(row.id);
    setDetailsOpen(true);
  }

  function handleCloseDetails() {
    setDetailsOpen(false);
    setDetailsId(null);
  }

  function handleCloseAssignModal() {
    setAssignModalOpen(false);
    setAssignCampus(null);
    setAssignRoleType("vp");
  }

  const handleAssignVP = async ({ campusId, userId }) => {
    try {
      const response = await assignCampusVP({ campusId, userId }).unwrap();
      toast.success(response?.message || "Campus VP assigned successfully");
      handleCloseAssignModal();
    } catch (error) {
      if (isSessionExpiredApiError(error)) return;
      console.error(error);
      toast.error(getApiErrorMessage(error) || "Failed to assign Campus VP");
    }
  };

  const handleAssignIncharge = async ({ campusId, userId }) => {
    try {
      const response = await assignStoreIncharge({
        campusId,
        userId,
      }).unwrap();
      toast.success(
        response?.message || "Store Incharge assigned successfully",
      );
      handleCloseAssignModal();
    } catch (error) {
      if (isSessionExpiredApiError(error)) return;
      console.error(error);
      toast.error(
        getApiErrorMessage(error) || "Failed to assign Store Incharge",
      );
    }
  };

  const handleUpdateVP = async ({ campusId, userId }) => {
    try {
      const response = await updateCampusVP({ campusId, userId }).unwrap();
      toast.success(response?.message || "Campus VP changed successfully");
      handleCloseAssignModal();
    } catch (error) {
      if (isSessionExpiredApiError(error)) return;
      console.error(error);
      toast.error(getApiErrorMessage(error) || "Failed to change Campus VP");
    }
  };

  const handleUpdateIncharge = async ({ campusId, userId }) => {
    try {
      const response = await updateStoreIncharge({
        campusId,
        userId,
      }).unwrap();
      toast.success(
        response?.message || "Store Incharge changed successfully",
      );
      handleCloseAssignModal();
    } catch (error) {
      if (isSessionExpiredApiError(error)) return;
      console.error(error);
      toast.error(
        getApiErrorMessage(error) || "Failed to change Store Incharge",
      );
    }
  };

  const handleSwapRoles = async (campusId) => {
    try {
      const response = await swapCampusRoles(campusId).unwrap();
      toast.success(
        response?.message ||
          "Campus VP and Store Incharge swapped successfully",
      );
    } catch (error) {
      if (isSessionExpiredApiError(error)) return;
      console.error(error);
      toast.error(getApiErrorMessage(error) || "Failed to swap campus roles");
    }
  };

  const rows = filteredCampuses.map((campus) => ({
    id: campus._id,
    name: campus.name,
    subtitle: campus.address,
    vp: campus.campusVp,
    incharge: campus.campusStoreIncharge,
  }));

  const loadingRows = Array.from({ length: 5 }, (_, i) => ({
    id: i,
  }));
  const displayRows = isLoading ? loadingRows : rows;

  const renderRow = (r, idx) => {
    if (isLoading) {
      return (
        <div key={idx} className="bg-[#EEEEEE40]">
          <div className="grid grid-cols-12 gap-4 px-7 py-6 items-center border-b border-[#E5E5E5]">
            <div className="col-span-4 h-10 bg-gray-200 rounded animate-pulse" />
            <div className="col-span-4 h-10 bg-gray-200 rounded animate-pulse" />
            <div className="col-span-3 h-10 bg-gray-200 rounded animate-pulse" />
            <div className="col-span-1 h-10 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>
      );
    }

    return (
      <div
        key={idx}
        className="bg-[#EEEEEE40]"
      >
        <div className="grid grid-cols-12 gap-4 px-7 py-6 items-center border-b border-[#E5E5E5] ">
          <div className="col-span-4 flex items-center gap-[14px] text-black">
            <div className="w-[53px] h-[42px] flex justify-center items-center rounded bg-[#E4E3E5]">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13 2L2 7M12 3V22H7C5.114 22 4.172 22 3.586 21.414C3 20.828 3 19.886 3 18V7M12 7L22 12"
                  stroke="black"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 22H17C18.886 22 19.828 22 20.414 21.414C21 20.828 21 19.885 21 18V11.5M18 10V7M7 11H8M7 15H8M16 14H17M16.5 22V18"
                  stroke="black"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <div className="text-sm leading-5">{r.name}</div>
              <div className="flex items-center gap-[5px] text-[10px] leading-4 text-[#878686]">
                <LocationIcon />
                <p>{r.subtitle}</p>
              </div>
            </div>
          </div>

          <div className="col-span-4 flex justify-center">
            {r.vp == null ? (
              <button
                type="button"
                onClick={() => handleOpenAssign(r, "vp")}
                className="group flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-blue-600 transition-colors"
              >
                <AssignedVP />
                Assign VP
              </button>
            ) : (
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 rounded-full text-[10px] font-black uppercase tracking-widest border border-blue-100 dark:border-blue-500/20">
                  <AssignedVP2 />
                  Assigned
                </div>
                <button
                  type="button"
                  onClick={() => handleOpenAssign(r, "update_vp")}
                  className="px-2 py-1 text-[10px] font-bold text-gray-500 hover:text-blue-600 uppercase tracking-wider underline underline-offset-2"
                >
                  Change
                </button>
              </div>
            )}
          </div>

          <div className="col-span-3">
            {r.incharge == null ? (
              <button
                type="button"
                onClick={() => handleOpenAssign(r, "incharge")}
                className="group flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-blue-600 transition-colors"
              >
                <AssignedVP />
                Assign Incharge
              </button>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 px-3 py-1 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-full text-[10px] font-black uppercase tracking-widest border border-emerald-100 dark:border-emerald-500/20">
                    <Incharge />
                    Assigned
                  </div>
                  <button
                    type="button"
                    onClick={() => handleOpenAssign(r, "update_incharge")}
                    className="px-2 py-1 text-[10px] font-bold text-gray-500 hover:text-emerald-600 uppercase tracking-wider underline underline-offset-2"
                  >
                    Change
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="col-span-1 flex justify-end items-center gap-3">
            <button aria-label="view" onClick={() => handleOpenDetails(r)} className="text-gray-500 hover:text-[#6D4E8A]">
              <Eye size={20} />
            </button>
            {r.vp != null && r.incharge != null && (
              <button
                type="button"
                aria-label="swap roles"
                title="Swap VP and Store Incharge"
                disabled={isSwappingRoles}
                onClick={() => handleSwapRoles(r.id)}
                className="disabled:opacity-50"
              >
                <ArrowLeftRight size={18} />
              </button>
            )}
            <button type="button" aria-label="edit" onClick={() => handleOpenEdit(r)}>
              <Edit />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="pt-6 md:pt-14 px-4 md:pl-8 md:pr-12 min-h-screen">
      <HeaderInfo
        title="Campus Network"
        desc="Oversee all branches and manage executive leadership assignments"
        btnLabel="Enroll New Campus"
        isSearchBar={true}
        onAdd={handleOpenAdd}
        searchPlaceholder="Search Campuses by name or address"
        searchValue={search}
        onSearchChange={setSearch}
        showFilterButton={false}
      />
      <div className="mt-6 ml-0 md:ml-2.5">
        <DataTable
          columns={[
            {
              key: "identity",
              label: "Campus Identity",
              className: "col-span-4",
            },
            {
              key: "vice",
              label: "Vice Principal",
              className: "col-span-4 text-center",
            },
            { key: "store", label: "Store Incharge", className: "col-span-3" },
            {
              key: "actions",
              label: "Actions",
              className: "col-span-1 text-right",
            },
          ]}
          rows={displayRows}
          renderRow={renderRow}
          page={page}
          totalPages={totalPages}
          onNext={handleNext}
          onPrev={handlePrev}
          isLoading={isLoading}
        />
      </div>

      <CampusModal
        open={modalOpen}
        mode={modalMode}
        initialValues={selectedCampus}
        onClose={handleCloseModal}
        isLoading={isSubmitting}
        onSubmit={async (val) => {
          try {
            if (modalMode === "add") {
              await createCampus({
                name: val.name,
                address: val.address,
              }).unwrap();
              toast.success("Campus created successfully");
            } else {
              await updateCampus({
                id: selectedCampus.id,
                name: val.name,
                address: val.subtitle,
              }).unwrap();
              toast.success("Campus updated successfully");
            }
            handleCloseModal();
          } catch (err) {
            if (isSessionExpiredApiError(err)) return;
            console.error(err);
            toast.error(getApiErrorMessage(err) || "Failed to save campus");
          }
        }}
      />

      <AssignCampusRoleModal
        open={assignModalOpen}
        onClose={handleCloseAssignModal}
        campus={assignCampus}
        roleType={assignRoleType}
        isLoading={
          assignRoleType === "vp"
            ? isAssigningVP
            : assignRoleType === "incharge"
            ? isAssigningIncharge
            : assignRoleType === "update_vp"
            ? isUpdatingVP
            : isUpdatingIncharge
        }
        onSubmit={
          assignRoleType === "vp"
            ? handleAssignVP
            : assignRoleType === "incharge"
            ? handleAssignIncharge
            : assignRoleType === "update_vp"
            ? handleUpdateVP
            : handleUpdateIncharge
        }
      />

      <CampusDetailsModal open={detailsOpen} onClose={handleCloseDetails} campusId={detailsId} />

      <DeleteModal
        open={deleteOpen}
        onClose={handleCloseDelete}
        onCreate={() => console.log("delete campus", deleteRow)}
        modalName="Campus"
      />
    </div>
  );
}
